<template>
  <FormContainer
    title="Standard Item"
    :mode="mode"
    :edit="edit"
    :saving="saving"
    :historyRoute="`/standard-items/${itemType.id}/item/${id}/history`"
    collection="standardItems"
    :docID="id"
    @save="save"
    @cancel="cancel"
    @confirmDelete="confirmDeleteStandardItem"
    :createdBy="standardItem && edit ? standardItem.createdBy : null"
    :createdByDate="standardItem && edit ? standardItem.createdByDate : null"
    :updatedBy="standardItem && edit ? standardItem.updatedBy : null"
    :updatedByDate="standardItem && edit ? standardItem.updatedByDate : null"
    :fullscreen="fullscreen"
    :denseNotes="true"
    >
    <div slot="title" class="item-form-title">
      <template
        v-if="mode==='dialog' && ($vuetify.breakpoint.smAndUp || !edit)"
        v-for="(t, index) in title
      ">
        <span
          :key="`${index}-span`"
          class="item-form-title__item-name"
        >{{ t }}</span>
        <v-icon
          :key="`${index}-icon`"
        >mdi-chevron-right</v-icon>
      </template>

      <template v-if="edit">
        <template v-if="standardItem.name && standardItem.name.length">
          <span class="item-form-title__item-name">{{ standardItem.name }}</span>
          <v-icon v-if="$vuetify.breakpoint.smAndUp">mdi-chevron-right</v-icon>
        </template>
        <span class="orange--text" v-if="$vuetify.breakpoint.smAndUp">Edit</span>
      </template>
        <span v-else class="green--text">New</span>
    </div>

    <v-container
      slot="body"
      fluid
      grid-list-md
      :class="[
        'pt-0 pb-0',
        $vuetify.breakpoint.xsOnly && 'pl-2'
      ]"
      :style="{ boxSizing: 'border-box' }"
      >
      <v-layout row wrap>
        <!-- Item Types -->
        <v-flex xs12>
          <v-container fluid grid class="py-0 px-0">
            <v-layout row align-center wrap>
              <v-flex xs12 class="grey--text text--lighten-1 px-0 py-0">Item Types</v-flex>
              <template v-for="(type, index) in itemTypes">
                <v-icon
                  v-if="index > 0"
                  v-show="$vuetify.breakpoint.smAndUp"
                  :key="type.key + 'icon'"
                  medium color="grey"
                  class="type-select-icon"
                  >mdi-chevron-right</v-icon>
                <v-select
                  :class="[
                    'item-type__select',
                    'input--dense',
                    $vuetify.breakpoint.smAndDown && 'item-type__select--full-width'
                  ]"
                  :menu-props="{
                      contentClass: `item-type__select__content--${type.key}`
                    }"
                  :key="type.key"
                  v-model="type.selection"
                  item-value="id"
                  item-text="name"
                  return-object
                  :filter="() => true"
                  required
                  dense
                  :items="index > 0
                    ? itemTypes[index - 1].children
                    : rootTypesByLevel"
                  :error-messages="itemTypesErrors[index]"
                  @input="itemTypesInput(type, index, $event)"
                  v-select-width="index > 0
                    ? itemTypes[index - 1].children
                    : rootTypesByLevel"
                >
                </v-select>
              </template>
            </v-layout>
          </v-container>
        </v-flex>

        <!-- CUSTOM PROPERTIES -->
        <template v-if="fields && fields.length">
          <FormField
            v-for="(field, index) in fields"
            :key="itemType.id + field.key"
            :field="field"
            :fieldKey="field.key"
            :fieldIndex="index"
            :errorMessages="extendedErrors[field.key]"
            :value="itemTypeFields[field.key]"
            @customInput="itemTypeFieldInput({ event: $event, field: field, index: index })"
            @customBlur="itemTypeFieldInput({ event: $event, field: field, index: index })"
          />
        </template>

        <!-- Description -->
        <v-flex xs12>
          <v-textarea
            label="Description"
            class="input--dense"
            row-height="12"
            rows="3"
            v-model="description"
            :error-messages="descriptionErrors"
            @input="$v.description.$touch()"
            @blur="$v.description.$touch()"
            ></v-textarea>
        </v-flex>

        <!-- Multiple Manufacturers -->
        <v-flex xs12 class="sub-form sub-form--dense pb-0" v-if="hasManyManufacturers">
          <v-expansion-panel v-model="manufacturersExpand">
            <v-expansion-panel-content>
              <div slot="header">
                <v-icon color="white">mdi-factory</v-icon>
                <span class="subheading ml-3 white--text">Manufacturers</span>
                <span  class="ml-1 grey--text text--lighten-1">{{ `(${manufacturers.length})` }}</span>
              </div>
              <v-card>
                <v-card-text class="px-0  py-1">
                  <v-container fluid grid class="px-0 py-1" v-show="manufacturers.length">
                    <transition-group name="slide-x-transition" tag="div" class="layout row wrap align-center">
                      <v-flex
                        v-for="(manufacturer, index) in manufacturers"
                        :key="manufacturer.key"
                        xs12
                        >
                        <v-container fluid grid class="py-2 px-2">
                          <v-layout row align-center>
                            <div class="mr-1 grey--text text--lighten-1 list__number">{{ index + 1}}.</div>

                            <!-- Inputs Container -->
                            <v-container fluid grid class="flex py-0 px-0">
                              <v-layout row wrap>
                                <!-- Manufacturer -->
                                <v-flex xs12 md4>
                                 <v-combobox
                                  label="Manufacturer"
                                  v-model.trim="manufacturer.manufacturerName"
                                  autocomplete
                                  clearable
                                  :items="manufacturerNames"
                                  combobox
                                  dense
                                  required
                                  class="input--dense"
                                  :error-messages="$v.manufacturers.$each[index].manufacturerName.$dirty && manufacturersErrors[index] ? manufacturersErrors[index].manufacturerName : []"
                                  @input="manufacturersInput({ event: $event, field: 'manufacturerName', index: index, key: manufacturer.key  })"
                                  @blur="$v.manufacturers.$each[index].manufacturerName.$touch()"
                                  :ref="`${manufacturer.key}__name-input`"
                                  >
                                  </v-combobox>
                                </v-flex>
                                <!-- Manufacturer SKU/Product # -->
                                <v-flex xs12 md4>
                                  <v-text-field
                                    label="Manufacturer Part No."
                                    required
                                    class="input--dense"
                                    v-model.trim="manufacturer.partNumber"
                                    :error-messages="$v.manufacturers.$each[index].partNumber.$dirty && manufacturersErrors[index] ? manufacturersErrors[index].partNumber : []"
                                    @input="manufacturersInput({ event: $event, field: 'partNumber', index: index, key: manufacturer.key  })"
                                    @blur="$v.manufacturers.$each[index].partNumber.$touch()"
                                  ></v-text-field>
                                </v-flex>

                                <!-- CE # -->
                                <v-flex xs12 md4>
                                  <v-text-field
                                    label="CE No."
                                    class="input--dense"
                                    v-model.trim="manufacturer.computerEaseNumber"
                                    :error-messages="$v.manufacturers.$each[index].computerEaseNumber.$dirty && manufacturersErrors[index] ? manufacturersErrors[index].computerEaseNumber : []"
                                    @input="manufacturersInput({ event: $event, field: 'computerEaseNumber', index: index, key: manufacturer.key  })"
                                    @blur="$v.manufacturers.$each[index].computerEaseNumber.$touch()"
                                  ></v-text-field>
                                </v-flex>
                              </v-layout>
                            </v-container>

                            <v-btn
                              @click="manufacturers.splice(index, 1)"
                              color="error"
                              class="delete-manufacturer-btn mx-0 my-0"
                              flat
                              icon>
                              <v-icon>mdi-close</v-icon>
                            </v-btn>
                          </v-layout>
                        </v-container>
                      </v-flex>
                    </transition-group>
                  </v-container>

                  <v-container
                    v-show="!manufacturers.length"
                    fluid
                    grid
                    class="px-3 pt-3 pb-0"
                  >
                    <v-layout column>
                      <v-flex xs12>
                        <span class="caption grey--text">There aren't any Manufacturers here yet</span>
                      </v-flex>
                    </v-layout>
                  </v-container>

                  <div class="error--text" v-if="$v.manufacturers.$dirty">
                    <span
                      v-for="error in manufacturersListErrors"
                      :key="error"
                    >{{ error }}</span>
                  </div>
                </v-card-text>

                <v-card-actions key="actions">
                  <v-container fluid grid class="px-3 pt-0 pb-2">
                    <v-layout row wrap justify-end>
                      <v-btn
                      color="success"
                      @click="addManufacturer"
                      outline
                      small
                      >Add</v-btn>
                    </v-layout>
                  </v-container>
                </v-card-actions>
              </v-card>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-flex>

        <!-- Multiple Vendors -->
        <v-flex xs12 class="sub-form sub-form--dense pb-0">
          <v-expansion-panel v-model="vendorsExpand">
            <v-expansion-panel-content>
              <div slot="header">
                <v-icon color="white">mdi-store</v-icon>
                <span class="subheading ml-3 white--text">Vendors</span>
                <span  class="ml-1 grey--text text--lighten-1">{{ `(${vendors.length})` }}</span>
              </div>
              <v-card>
                <v-card-text class="px-0  py-1">
                  <v-container fluid grid class="px-0 py-1" v-show="vendors.length">
                    <transition-group name="slide-x-transition" tag="div" class="layout row wrap align-center">
                      <v-flex
                        v-for="(vendor, index) in vendors"
                        :key="vendor.key"
                        xs12
                        >
                        <v-container fluid grid class="pa-0">
                          <v-layout row align-center>
                            <div class="mr-1 grey--text text--lighten-1 list__number">{{ index + 1}}.</div>

                            <!-- Inputs Container -->
                            <v-container fluid grid class="flex py-0 px-0">
                              <v-layout row wrap>
                                <!-- Vendor -->
                                <v-flex xs12 md6>
                                 <v-combobox
                                  label="Vendor"
                                  v-model.trim="vendor.vendorName"
                                  autocomplete
                                  clearable
                                  :items="vendorNames"
                                  combobox
                                  dense
                                  required
                                  class="input--dense"
                                  :error-messages="$v.vendors.$each[index].vendorName.$dirty && vendorsErrors[index] ? vendorsErrors[index].vendorName : []"
                                  @input="vendorsInput({ event: $event, field: 'vendorName', index: index, key: vendor.key  })"
                                  @blur="$v.vendors.$each[index].vendorName.$touch()"
                                  :ref="`${vendor.key}__name-input`"
                                  >
                                  </v-combobox>
                                </v-flex>
                                <!-- Vendor SKU/Product # -->
                                <v-flex xs12 md6>
                                  <v-text-field
                                    label="Vendor Part No."
                                    required
                                    class="input--dense"
                                    v-model.trim="vendor.partNumber"
                                    :error-messages="$v.vendors.$each[index].partNumber.$dirty && vendorsErrors[index] ? vendorsErrors[index].partNumber : []"
                                    @input="vendorsInput({ event: $event, field: 'partNumber', index: index, key: vendor.key  })"
                                    @blur="$v.vendors.$each[index].partNumber.$touch()"
                                  ></v-text-field>
                                </v-flex>
                              </v-layout>
                            </v-container>

                            <v-btn
                              @click="vendors.splice(index, 1)"
                              color="error"
                              class="delete-manufacturer-btn mx-0 my-0"
                              flat
                              icon>
                              <v-icon>mdi-close</v-icon>
                            </v-btn>
                          </v-layout>
                        </v-container>
                      </v-flex>
                    </transition-group>
                  </v-container>

                  <v-container
                    v-show="!vendors.length"
                    fluid
                    grid
                    class="px-3 pt-3 pb-0"
                  >
                    <v-layout column>
                      <v-flex xs12>
                        <span class="caption grey--text">There aren't any Vendors here yet</span>
                      </v-flex>
                    </v-layout>
                  </v-container>

                  <div class="error--text" v-if="$v.vendors.$dirty">
                    <span
                      v-for="error in vendorsListErrors"
                      :key="error"
                    >{{ error }}</span>
                  </div>
                </v-card-text>

                <v-card-actions key="actions">
                  <v-container fluid grid class="px-3 pt-0 pb-2">
                    <v-layout row wrap justify-end>
                      <v-btn
                      color="success"
                      @click="addVendor"
                      outline
                      small
                      >Add</v-btn>
                    </v-layout>
                  </v-container>
                </v-card-actions>
              </v-card>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-flex>
      </v-layout>
    </v-container>
  </FormContainer>
</template>

<script>
import {
  assign,
  assignAll,
  assignIn,
  clone,
  cloneDeep,
  isArray,
  isEqual,
  isNaN,
  isNil,
  isNull,
  isNumber,
  isString,
  get,
  getOr,
  has,
  maxBy,
  merge,
  mergeAll,
  pick,
  set,
  sortBy,
  toNumber,
  uniqueId
} from 'lodash/fp'

import { createMailTo } from '@/utils'
import { required } from 'vuelidate/lib/validators'
import { mapGetters, mapActions } from 'vuex'

import FormContainer from '@/components/Form/FormContainer'
import FormField from './FormField'

import Manufacturers from './mixins/Manufacturers'
import Vendors from './mixins/Vendors'

const touchMap = new WeakMap()

export default {
  name: 'StandardItemForm',
  mixins: [Manufacturers, Vendors],
  props: ['edit', 'mode', 'itemDialog', 'fullscreen'],
  components: {
    FormContainer,
    FormField
  },
  data () {
    return {
      deleteId: '',
      standardItem: null,
      saving: false,
      itemTypes: [],
      description: '',
      itemTypeFields: {},
      fields: [],
      extendedErrors: {}
    }
  },

  validations () {
    let finalValidation = {}

    let baseValidations = {
      itemTypes: {
        required,
        $each: {
          selection: {
            required
          }
        }
      },
      description: {},
      ...cloneDeep(this.vendorValidations())
    }
    finalValidation = { ...baseValidations }

    if (this.fields && this.fields.length) {
      const fields = this.fields

      let validations = fields.reduce((acc, curr) => {
        let field = curr
        let validation = {}

        if (field.validation) {
          let fieldValidations = field.validation.reduce((ac, cur) => {
            ac[cur.validationKey] = cur.validationFunction
            return ac
          }, {})
          validation = { ...validation, ...fieldValidations }
        }

        if (field.required) {
          validation = { ...validation, required }
        }

        acc[field.key] = validation
        return acc
      }, {})

      console.log('returning validations: ', baseValidations, validations)
      finalValidation = { ...finalValidation, itemTypeFields: cloneDeep(validations) }
    }

    if (this.hasManyManufacturers) {
      const manufacturerValidations = this.manufacturerValidations()
      finalValidation = { ...finalValidation, ...cloneDeep(manufacturerValidations) }
    }

    return finalValidation
  },
  computed: {
    hasManyManufacturers () {
      return this.fields && this.fields.findIndex(field => field.value === 'manufacturers') >= 0
    },
    mailTo () {
      if (this.standardItem) {
        let body = []
        for (const key in this.standardItem) {
          body.push({
            label: key,
            text: this.standardItem[key]
          })
        }
        return createMailTo({
          from: this.user.email,
          subject: this.standardItem.name,
          body
        })
      } else return null
    },
    title () {
      let title = []
      title.push(this.itemType.name)
      return title
    },

    // Create an array of fields to compare when saving an updated Item
    updateComparisonProperties () {
      const defaultProperties = [ 'description', 'vendors', 'itemTypes' ]
      const fieldProperties = this.fields.reduce((acc, curr) => {
        if (!has('trackUpdates')(curr)) acc.push(curr.key)
        else if (isArray(get('trackUpdates')(curr))) acc = [...acc, ...get('trackUpdates')(curr)]
        return acc
      }, [])

      return [ ...defaultProperties, ...fieldProperties ]
    },

    itemTypesToSave () {
      return this.itemTypes.reduce((acc, curr) => {
        if (curr.selection) {
          acc[curr.selection.id] = pick([
            'id',
            'level',
            'name',
            'timestamp',
            'dateCreated'
          ])(curr.selection)
        }

        return acc
      }, {})
    },

    itemTypesErrors () {
      console.log('computing itemTypesErrors')
      console.log('this.$v.itemTypes', this.$v.itemTypes)
      let errors = []

      console.log('this.$v.itemTypes.$each', this.$v.itemTypes.$each)

      for (let index in this.$v.itemTypes.$each) {
        if (isNaN(parseInt(index, 10))) continue

        console.log('index: ', index)
        console.log('this.$v.itemTypes.$each[index]: ', this.$v.itemTypes.$each[index])
        errors[index] = []

        if (!this.$v.itemTypes.$each[index].$dirty) continue

        if (!this.$v.itemTypes.$each[index].selection.required) {
          errors[index].push(index > 0 ? 'Sub Type is required' : 'An item type is required')
        }
      }

      console.log('RETURNING COMPUTED ERRORS: ', errors)

      return errors
    },

    descriptionErrors () {
      return []
    },

    ...mapGetters({
      dark: 'user/theme',
      user: 'user/saveUser',
      standardItems: 'standardItems/items/all',
      getItem: 'standardItems/items/standardItem',
      itemType: 'standardItems/itemTypes/currentType',
      currentTypeParents: 'standardItems/itemTypes/currentTypeParents',
      currentTypeChildren: 'standardItems/itemTypes/children/currentChildren',
      typeChildren: 'standardItems/itemTypes/getTypeChildren',
      id: 'standardItems/items/currentID',
      currentItem: 'standardItems/items/currentItem',
      hasNoteToSave: 'notes/hasNoteToSave',
      rootTypesByLevel: 'standardItems/itemTypes/rootTypesByLevel'
    })
  },
  watch: {
    itemType: {
      handler (newValue, oldValue) {
        console.log('itemTypes WATCH')
        console.log('oldValue: ', oldValue)
        console.log('newValue: ', newValue)
        this.fillItemTypes()
        this.makeFields()
      },
      deep: true
    },

    itemDialog (newValue, oldValue) {
      console.log('itemDialog Watch')
      console.log('newValue', newValue)
      console.log('oldValue', oldValue)
      if (this.mode === 'dialog' && newValue) {
        console.log('mode is dialog and is open')
        this.fillForm(this.currentItem)
      }
    },

    id (newId, oldId) {
      if (this.edit && newId) {
        const standardItem = this.currentItem
        this.fillForm(standardItem)

        this.$v.$reset()
      }

      this.fillItemTypes()
    },

    '$v': {
      handler (newValue, oldValue) {
        if (this.fields && this.fields.length) {
          this.fields.forEach(field => {
            let fieldErrors = []
            if (field.required && !this.$v.itemTypeFields[field.key].required && this.$v.itemTypeFields[field.key].$dirty) {
              fieldErrors.push(`${field.attributes.label} is required`)
            }

            if (field.validation) {
              field.validation.forEach(validation => {
                if (!this.$v.itemTypeFields[field.key][validation.validationKey] &&
                this.$v.itemTypeFields[field.key].$dirty) {
                  fieldErrors.push(validation.errorMessage || 'error')
                }
              })
            }

            this.$set(this.extendedErrors, field.key, fieldErrors)
          })
        }
      },
      deep: true
    }
  },
  methods: {
    makeFields () {
      if (!this.itemType) {
        this.fields = []
        Object.assign(this.itemTypeFields, {})
        return
      }

      const fields = this.itemType && this.itemType.form ? cloneDeep(this.itemType.form) : []

      if (fields && fields.length) {
        console.log('adding item Type fields: ', this.fields)
        this.fields = [ ...fields ]

        this.fields.forEach(field => this.$set(this.itemTypeFields, field.key, field.default || null))
      }
    },

    delayTouch ($v, timeout = 1000) {
      $v.$reset()
      if (touchMap.has($v)) {
        clearTimeout(touchMap.get($v))
      }
      touchMap.set($v, setTimeout($v.$touch, timeout))
    },

    itemTypesInput (type, index, event) {
      console.log('!!! itemTypesInput !!!')
      console.log('type', type)
      console.log('typeID: ', event.id)
      console.log('index: ', index)
      console.log('event', event)

      // Check if the new value is Null, clear all children
      if (isNull(event)) {
        console.log('new value is null, remove all subtypes')
        this.itemTypes.splice(index + 1)
        return
      }

      /* Check if the Type has changed from its previous value
      if (_.get(this.itemTypes[index], ['selection', 'id']) === event.id) {
        console.log('itemType has not changed, do nothing')
        return
      }
      */

      // Remove any Type decendants that were selected
      if (index !== this.itemTypes.length - 1) this.itemTypes.splice(index + 1)

      // Get the children for the selected Type
      const children = this.typeChildren(event.id)

      // Add them as choices for the next Type
      if (children && children.length) {
        this.itemTypes[index].children = sortBy(child => child.name)(children)

        this.itemTypes.push({
          selection: null,
          children: [],
          key: uniqueId()
        })
      }

      console.groupEnd()
    },

    confirmDeleteStandardItem () {
      console.log('confirmDeleteStandardItem')
      console.log('this.standardItem', this.standardItem)
      console.log('this.standardItem.id', this.standardItem.id)
      if (this.standardItem && this.standardItem.id) {
        console.log('dispatching delete')
        this.deleteStandardItem(this.standardItem.id)
        this.deleteDialog = false
        this.cancel()
      }
    },

    itemTypeFieldInput ({ event, field }) {
      console.log('itemTypeFieldInput')
      console.log('event: ', event)
      console.log('field: ', field)

      const existingValue = this.itemTypeFields[field.key]

      if (isEqual(existingValue, event)) return null

      this.itemTypeFields[field.key] = event

      if (field.delayTouch) {
        this.delayTouch(this.$v.itemTypeFields[field.key])
      } else {
        this.$v.itemTypeFields[field.key].$touch()
      }
    },

    clearForm () {
      this.saving = false
      this.description = ''

      // Item Type Fields
      console.group('filling custom fields')
      if (this.fields && this.fields.length) {
        this.fields.forEach(field => {
          console.group('field: ', field.key)
          let fillValue = get(field.value)(this.standardItem)
          console.log('fillValue: ', fillValue)
          console.groupEnd()
          this.itemTypeFields[field.key] = fillValue || null
        })
      }
      console.groupEnd()

      // this.itemTypes = []
    },

    fillForm (standardItem) {
      console.log('filling form', standardItem)
      this.standardItem = cloneDeep(standardItem)

      this.description = has('description')(this.standardItem)
        ? this.standardItem.description
        : null

      // Item Type Fields
      console.group('filling custom fields')
      if (this.fields && this.fields.length) {
        this.fields.forEach(field => {
          console.group('field: ', field.key)
          let fillValue = get(field.value)(this.standardItem)
          console.log('fillValue: ', fillValue)
          console.groupEnd()
          this.itemTypeFields[field.key] = fillValue || null
        })
      }
      console.groupEnd()

      this.hasManyManufacturers && this.fillManufacturers(standardItem)

      // Vendors
      this.fillVendors(standardItem)

      this.fillItemTypes()
    },

    fillItemTypes () {
      // Item Types
      let itemType = this.itemType
      console.log('itemType: ', itemType)
      let itemTypes = []

      const parents = this.currentTypeParents
      console.log('parents: ', parents)
      if (itemType.level !== 0 && parents && parents.length) {
        parents.forEach((parent, parentIndex) => {
          console.log('parent: ', parent)
          const children = sortBy(child => child.name)(this.typeChildren(parent.id))
          console.log('children for parent: ', children)

          itemTypes.push({
            selection: parent,
            children,
            key: uniqueId()
          })
        })
      }

      itemTypes.push({
        selection: clone(this.itemType),
        children: clone(this.currentTypeChildren),
        key: uniqueId()
      })
      console.log('itemTypes', itemTypes)
      this.itemTypes = sortBy(type => type.selection.level)(itemTypes)
    },

    async getItemTypeFieldValue (field) {
      // let fieldKey = field.key
      console.log('field: ', field.key)
      // Get the current value from the form
      let fieldValue = get(field.key)(this.itemTypeFields) || null

      // cast value to field type
      switch (field.type) {
        case 'float':
          // Cast float fields to floats if possible
          const numValue = toNumber(fieldValue)
          fieldValue = fieldValue &&
          !isNil(fieldValue) &&
          !isNull(fieldValue) &&
          isNumber(numValue) &&
          !isNaN(numValue)
            ? numValue
            : fieldValue
          break

        case 'checkbox':
          // cast null checkbox values to false
          fieldValue = isNil(fieldValue) || isNull(fieldValue)
            ? false
            : fieldValue
          break

        default:
          break
      }

      let value = { [field.value]: fieldValue }
      console.log('value: ', value)

      // If the field is a combobox and allows saving new values, do that
      const saveOptions = field.saveOptions
      if (saveOptions && saveOptions.saveNew && fieldValue) {
        console.log('SaveNew option exists, check for existing values')
        value = fieldValue
        console.log('value', value)
        let findValue = isString(value) ? value : getOr(value, saveOptions.existingGetterParameter, value)
        console.log('findValue: ', findValue)

        const existingValue = this.$store.getters[saveOptions.existingGetter](findValue)
        console.log('exisitingValue: ', existingValue)
        console.log('set: ', set)
        console.log('assign: ', assign)
        console.log('assignIn:', assignIn)
        console.log('assignAll: ', assignAll)
        console.log('merge: ', merge)
        // If it already exists, map its values to the new standardItem
        if (existingValue) {
          console.log('value exists, save exisiting')
          let newValue = {}
          saveOptions.saveMap.forEach(saveMapField => {
            newValue[saveMapField.set] = getOr(existingValue, saveMapField.get, existingValue)
          })
          value = newValue
          console.log('value after map: ', value)
        } else { // Else, save the new value, and use the returned id
          console.log('Value does not exist, need to save first')
          // Make a new value from the createMap
          let newValue = {}
          saveOptions.createMap.forEach(createMapField => {
            newValue[createMapField.set] = getOr(value, createMapField.get, value)
          })
          console.log('newValue: ', newValue)

          // Try to save the new value and get the new Document ID back
          let newID = await this.$store.dispatch(saveOptions.createAction, newValue)
          console.log('newID', newID)

          // Once it is saved, get its full value from the store
          let savedValue = this.$store.getters[saveOptions.newGetter](newID)
          console.log('savedValue', savedValue)
          console.log('saveMap: ', saveOptions.saveMap)

          // Map the value to the standardItem based on the saveMap
          let mappedSavedValue = {}
          saveOptions.saveMap.forEach(saveMapField => {
            mappedSavedValue[saveMapField.set] = getOr(savedValue, saveMapField.get, savedValue)
          })
          value = mappedSavedValue
          console.log('value after map: ', value)
        }
      } else if (field.value === 'manufacturers') {
        value = await this.saveManufacturers()
      }
      // Store the value to customValues
      return value
    },

    async save () {
      this.$v.$touch()

      console.log('touchMap', touchMap)
      let validation = Object.freeze(cloneDeep(this.$v))
      console.log('frozen validation: ', validation)
      console.log('Touched validation: ', this.$v)
      console.log('this.$v.$error: ', this.$v.$error)
      console.log('this.$v.$anyError', this.$v.$anyError)
      if (
        this.$v.$error ||
        this.$v.$anyError ||
        this.$v.itemTypeFields.$error ||
        this.$v.itemTypeFields.$anyError ||
        this.$v.vendors.$error ||
        this.$v.vendors.$anyError
      ) {
        console.log('THERES A FUCKING ERROR, STOP')
        if (
          (this.$v.manufacturers && this.$v.manufacturers.$error) ||
          (this.$v.manufacturers && this.$v.manufacturers.$anyError)
        ) {
          this.manufacturersExpand = 0
        }

        if (
          (this.$v.vendors.$error) ||
          (this.$v.vendors.$anyError)
        ) {
          this.vendorsExpand = 0
        }

        return null
      } else if (
        (this.$v.manufacturers && this.$v.manufacturers.$error) ||
        (this.$v.manufacturers && this.$v.manufacturers.$anyError)
      ) {
        console.log('THERES A Manufacturers ERROR, STOP')
        return null
      } else {
        console.log('\n\n ~~~~~~~~~~~~~~~~~~ passed erorr check ~~~~~~~~~~~~~~~~~~\n\n')

        this.saving = true

        console.log('ITEM TYPES TO SAVE: ', this.itemTypesToSave)

        // Base Item to save
        let standardItem = {
          description: this.description,
          types: this.itemTypesToSave
        }

        console.log('standardItem before Custom: ', standardItem)

        // Merge Custom Properties
        if (this.fields && this.fields.length) {
          let customValuePromises = this.fields.reduce((acc, curr) => {
            acc.push(this.getItemTypeFieldValue(curr))
            return acc
          }, [])

          console.log('customValuePromises', customValuePromises)
          const customValueResults = await Promise.all(customValuePromises)
          console.log('customValueResults', customValueResults)

          standardItem = mergeAll(customValueResults, standardItem)
          console.log('standardItems item after custom fields: ', standardItem)
        }

        // Add Vendors
        standardItem['vendors'] = await this.saveVendors()

        if (this.edit) {
          console.log('SAVING AN EDITED standardItems ITEM')
          console.log('SAVING ITEM: ', standardItem)

          // Compare to Original
          let original = cloneDeep(pick(this.updateComparisonProperties)(this.standardItem))
          let updatedItem = cloneDeep(pick(this.updateComparisonProperties)(standardItem))

          console.log('original ', original)
          const hasNoteToSave = this.hasNoteToSave
          if (isEqual(original, updatedItem) && !hasNoteToSave) {
            console.log('no changes were made, ignore save')
            this.cancel()
            return null
          }

          this.update({
            id: this.id,
            standardItem
          })
            .then(() => {
              console.log('standardItems item updated, dispatching save notes to id: ', this.id)
              this.addNewNote(this.id)
            })
            .then(() => {
              this.saving = false
              this.clearForm()
              this.$v.$reset()

              if (this.mode === 'dialog') {
                this.$emit('done')
              } else if (this.mode === 'route') {
                this.$router.back()
              }
            })
        } else {
          console.log('SAVING NEW standardItems ITEM', standardItem)
          console.log('this.add', this.add)

          this.add(standardItem)
            .then(docID => this.addNewNote(docID))
            .then(() => {
              this.saving = false
              this.clearForm()
              this.$v.$reset()

              if (this.mode === 'dialog') {
                this.$emit('done')
              } else if (this.mode === 'route') {
                this.$router.back()
              }
            })
        }
      }
    },
    cancel () {
      this.clearForm()
      this.$v.$reset()
      if (this.mode === 'dialog') {
        this.$emit('cancel')
      } else if (this.mode === 'route') {
        this.$router.back()
      }
    },

    ...mapActions({
      add: 'standardItems/items/add',
      update: 'standardItems/items/update',
      deleteStandardItem: 'standardItems/items/deleteStandardItem',
      addNewNote: 'notes/addNewNote'
    })
  },
  created () {
    console.log('INVETORY ITEM FORM CREATED')
    console.log('this: ', this)
    if (this.itemType) {
      this.fillItemTypes()
    }

    this.makeFields()

    if (this.edit && this.id) {
      const standardItem = this.currentItem
      this.fillForm(standardItem)
    }
  },
  mounted () {
    if (this.mode === 'dialog') this.$vuetify.goTo(this.$el)
  },
  directives: {
    'select-width': {
      inserted (el, binding, vnode) {
        // Item Type Selects are flexboxes, so they are only as wide as they need to be
        // However, this creates an issue when a new Type Select is created and has much longer item names
        //  than the standard min-width
        // This directive calculates a minimum width for the menu based on the length of the select items
        const STRING_CHAR_WIDTH = 7
        const MIN_MENU_WIDTH = 150
        console.log('select-width')
        console.log('el: ', el)
        console.log('vnode: ', vnode)
        const MAX_MENU_WIDTH = el.parentElement.offsetWidth

        // Array of possible choices for the select
        const selectItems = binding.value

        // Get the longest (largest) str of the items
        const longestItem = maxBy(item => item.name.length)(selectItems)

        // Calculate a new menu minWidth
        const minWidth = Math.max(longestItem.name.length * STRING_CHAR_WIDTH, MIN_MENU_WIDTH)
        const maxWidth = Math.min(minWidth, MAX_MENU_WIDTH)
        console.log('minWidth', minWidth)
        console.log('maxWidth', maxWidth)
        el.style.minWidth = `${maxWidth}px`
      }
    }
  }
}
</script>

<style lang="stylus">
@import '~vuetify/src/stylus/settings/_variables.styl'

$selectMinWidth = 150px

.item-form-title
  display: flex
  align-items: center
  flex-wrap: nowrap
  max-width: calc(100% - 88px)
  overflow: hidden

  .item-form-title__item-name
    white-space: nowrap
    text-overflow: ellipsis
    overflow: hidden

.item-type__select
  margin-top: 0
  flex: 1
  min-width: $selectMinWidth

  &&--full-width
    flex: 1 1 auto

.type-select-icon
  position: relative
  // top: -8px

</style>
